import React, {
    useEffect,
    useRef,
    useState,
    useImperativeHandle,
    forwardRef,
    useCallback,
  } from "react";
  import { GoChevronDown, GoChevronLeft } from "react-icons/go";
  import { AiOutlineClose, AiOutlineExclamationCircle } from "react-icons/ai";
  import { motion, AnimatePresence } from "framer-motion";
  import DatePicker from "react-multi-date-picker";
  import { IoCalendarOutline } from "react-icons/io5";
  import persian_fa from "react-date-object/locales/persian_fa";
 import persian from "react-date-object/calendars/persian";
  
  const CustomInput = forwardRef(
    (
      {
        label,
        items = [],
        setVal,
        setSubVal,
        val,
        type,
        textRows = 4,
        unitLabel = "تومان",
        error = false,
        Placeholder="ورودی...",

      },
      ref
    ) => {

      const [open, setOpen] = useState(false);
      const [expanded, setExpanded] = useState(null);
      const [displayValue, setDisplayValue] = useState(val);
      const [search, setSearch] = useState("");
      const [highlightedIndex, setHighlightedIndex] = useState(-1);
      const [shakeTrigger, setShakeTrigger] = useState(0);

      const inputRef = useRef(null);
      const wrapperRef = useRef(null);

 
      useImperativeHandle(ref, () => ({
        wrapper: wrapperRef.current,
        input: inputRef.current,
        scrollTo: () =>
          wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
        shake: () => setShakeTrigger((s) => s + 1),
        focus: () => inputRef.current?.focus(),
      }));
  
      const errorMessage = typeof error === "string" ? error : error ? "این فیلد الزامی است" : null;
      const hasError = Boolean(errorMessage);
  

      
      useEffect(() => {
        const onDocClick = (e) => {
          if (!wrapperRef.current?.contains(e.target)) {
            setOpen(false);
            setSearch("");
            setExpanded(null);
            setHighlightedIndex(-1);
          }
        };
  
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
      }, []);
  
      useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
      }, [open]);
  
      const norm = useCallback((s = "") => String(s).toLowerCase().trim(), []);
  
      const formatNumberForDisplay = (raw = "") => {
        if (raw === "" || raw == null) return "";
        const parts = String(raw).split(".");
        const intPart = parts[0].replace(/\D+/g, "") || "0";
        const fracPart = parts[1] ?? null;
        const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const formatted = fracPart != null ? `${withSep}.${fracPart.replace(/\D+/g, "")}` : withSep;
        return `${formatted}`;
      };
  
      const normalizeNumericRaw = (s = "") => {
        if (s == null) return "";
        s = s.replace(/تومان/g, "").replace(/,/g, "").trim();
        const firstDotIndex = s.indexOf(".");
        if (firstDotIndex === -1) {
          return s.replace(/\D+/g, "");
        } else {
          const before = s.slice(0, firstDotIndex).replace(/\D+/g, "");
          const after = s.slice(firstDotIndex + 1).replace(/\D+/g, "");
          return `${before}${after ? "." + after : ""}`;
        }
      };

      const filteredItems = React.useMemo(() => {
        if (type !== "option") return [];
        const q = norm(search);
        if (!q) return items ?? [];
  
        return (items ?? [])
          .map((it) => {
            if (!it) return null;
            const label = it.label ?? it.name ?? "";
            const itemMatches = norm(label).includes(q);
  
            const rawSubs = Array.isArray(it.subItems) ? it.subItems : Array.isArray(it.cities) ? it.cities : [];
            const subs = rawSubs
              .map((s) => {
                if (!s) return null;
                if (typeof s === "string") return { name: s, value: s };
                const name = s.name ?? s.title ?? s.value ?? "";
                const value = s.value ?? s.id ?? s._id ?? name ?? null;
                if (!name) return null;
                return { name, value };
              })
              .filter(Boolean);
  
            const matchingSubs = subs.filter((s) => norm(s.name).includes(q));
            if (itemMatches) return { ...it, subItems: subs };
            if (matchingSubs.length > 0) return { ...it, subItems: matchingSubs };
            return null;
          })
          .filter(Boolean);
      }, [items, search, type, norm]);
  

      useEffect(() => {
        setHighlightedIndex(filteredItems.length > 0 ? 0 : -1);
      }, [filteredItems]);
  
      const handleWrapperMouseDown = (e) => {
        if (type !== "option") return;
        e.preventDefault();
        setOpen((prev) => !prev);
        if (!open) {
          setTimeout(() => inputRef.current?.focus(), 0);
          setSearch("");
          setHighlightedIndex(0);
        }
      };
  
      const handleMainSelect = (item, idx) => {
        setDisplayValue(item.label);
        setVal?.(item.value);
        setSubVal?.(null);
  
        if (Array.isArray(item.subItems) && item.subItems.length > 0) {
          setExpanded((prev) => (prev === idx ? null : idx));
        } else {
          setOpen(false);
          setExpanded(null);
          setSearch("");
        }
      };
  
      const handleMainMouseDown = (e, item, idx) => {
        e.stopPropagation();
        e.preventDefault();
        handleMainSelect(item, idx);
      };
  
      const handleSubMouseDown = (e, parentLabel, sub) => {
        e.stopPropagation();
        e.preventDefault();
        setDisplayValue(`${parentLabel} , ${sub.name}`);
        setSubVal?.(sub.value);
        setOpen(false);
        setExpanded(null);
        setSearch("");
      };
  
      const handleClearMouseDown = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setDisplayValue(null);
        setVal?.(null);
        setSubVal?.(null);
        setSearch("");
        setOpen(false);
        setExpanded(null);
        setHighlightedIndex(-1);
      };

      const handleOptionKeyDown = (e) => {
        if (type !== "option") return;
        if (!open) return;
  
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setHighlightedIndex((hi) => Math.min((filteredItems.length || 1) - 1, Math.max(0, hi + 1)));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setHighlightedIndex((hi) => Math.max(0, hi - 1));
        } else if (e.key === "Enter") {
          e.preventDefault();
          const idx = highlightedIndex;
          const item = filteredItems?.[idx];
          if (item) {
            if (Array.isArray(item.subItems) && item.subItems.length > 0 && !(search && search.trim().length > 0)) {
              setExpanded((prev) => (prev === idx ? null : idx));
            } else {
              handleMainSelect(item, idx);
            }
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          setOpen(false);
          setSearch("");
          setExpanded(null);
          setHighlightedIndex(-1);
        }
      };
  
      const containerClass = "w-full max-w-[320px] sm:max-w-[280px] md:max-w-[260px] lg:max-w-[220px]";
  
      const shakeAnimation = {
        x: shakeTrigger || hasError ? [0, -6, 6, -4, 4, 0] : [0],
      };
  
      if (type === "option") {
        return (
          <motion.div
            ref={wrapperRef}
            animate={shakeAnimation}
            transition={{ duration: 0.56 }}
            className={`flex flex-col gap-2 ${containerClass}`}
            dir="rtl"
          >
        
            <label className="text-gray-700 text-[18px]">{label}</label>
  
            <div
              onMouseDown={handleWrapperMouseDown}
              className={`flex items-center justify-between w-full rounded-2xl h-[44px] px-3 cursor-text shadow-sm hover:shadow-md transition select-none bg-white border ${hasError ? "border-red-500" : "border-gray-300"}`}
            >
              <input
                ref={inputRef}
                value={ open ? search : displayValue ?? ""}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (!open) setOpen(true);
                }}
                onFocus={() => {
                  setOpen(true);
                  setSearch("");
                  setExpanded(null);
                }}
                onKeyDown={handleOptionKeyDown}
                placeholder={displayValue ? "" : "انتخاب کنید یا جستجو..."}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls="custom-input-listbox"
                className={`flex-1 outline-none text-[15px] ${open ? "text-gray-800" : displayValue ? "text-gray-800" : "text-gray-500"} bg-transparent`}
              />
  
              {displayValue ? (
                <AiOutlineClose
                  size={18}
                  className="text-gray-600 hover:text-gray-800"
                  onMouseDown={handleClearMouseDown}
                />
              ) : (
                <GoChevronDown
                  size={20}
                  className={`text-gray-600 transition-transform ${open ? "rotate-180" : ""}`}
                />
              )}
            </div>
  
            {hasError && (
              <div role="alert" className="flex items-center gap-2 mt-1 text-sm text-red-600">
                <AiOutlineExclamationCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}
  
            <AnimatePresence>
              {open && (
                <motion.div
                  onMouseDown={(e) => e.stopPropagation()} 
                  id="custom-input-listbox"
                  initial={{ opacity: 0, y: -6, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -6, height: 0 }}
                  transition={{ duration: 0.22 }}
                  className="mt-2 w-full max-h-[300px] overflow-y-auto rounded-xl border border-gray-200 shadow-md bg-white dropdown-scroll"
                  role="listbox"
                  aria-label={label}
                >
                  {filteredItems.length === 0 ? (
                    <div className="px-4 py-3 text-gray-500">موردی یافت نشد</div>
                  ) : (
                    filteredItems.map((item, idx) => {
                      const isSearchMode = Boolean(search && search.trim().length > 0);
                      const showExpanded = isSearchMode ? true : expanded === idx;
                      const isHighlighted = idx === highlightedIndex;
  
                      return (
                        <div key={idx} className="rtl" dir="rtl">
                          <div
                            onMouseDown={(e) => handleMainMouseDown(e, item, idx)}
                            onMouseEnter={() => setHighlightedIndex(idx)}
                            id={`option-${idx}`}
                            role="option"
                            aria-selected={isHighlighted}
                            className={`flex justify-between items-center px-4 py-3 hover:bg-gray-50 cursor-pointer text-gray-700 ${isHighlighted ? 'bg-gray-50' : ''}`}
                          >
                            <span className="text-[15px]">{item.label}</span>

                            {Array.isArray(item.subItems) && item.subItems.length > 0 ? (
                              <GoChevronLeft
                                className={`transition-transform ${showExpanded ? "rotate-90" : ""}`}
                              />
                            ) : null}
                          </div>
  
                          <AnimatePresence>
                            {showExpanded && Array.isArray(item.subItems) && item.subItems.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.18 }}
                                className="px-2 pb-3"
                              >
                                <div className="mt-2 space-y-2 px-2">
                                  {item.subItems.map((sub, sIdx) => (
                                    <div
                                      key={sIdx}
                                      onMouseDown={(e) => handleSubMouseDown(e, item.label, sub)}
                                      className="px-3 py-2 text-gray-600 hover:bg-gray-50 cursor-pointer rounded-md border border-gray-100"
                                    >
                                      {sub.name}
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      }
  
      /* ========================= DATE ========================= */
      if (type === "date") {
        return (
          <motion.div
            ref={wrapperRef}
            animate={shakeAnimation}
            transition={{ duration: 0.56 }}
            className={`flex flex-col w-full gap-2 ${containerClass}`}
            dir="rtl"
          >
            <label className="text-gray-700 text-[18px]">{label}</label>
  
            <div className={`relative w-full border px-4 py-2 rounded-xl bg-white ${hasError ? "border-red-500" : "border-gray-300"}`}>
              <DatePicker
              value={val}
                locale={persian_fa}
              calendar={persian}
                calendarPosition="bottom-right"
                placeholder="تاریخ را انتخاب کنید"
                inputClass="w-full text-gray-700 z-50 h-full rounded-xl bg-white focus:outline-none"
              onChange={(dateObj) => {
                  const shamsi = dateObj?.format?.("YYYY/MM/DD");
                  setDisplayValue(shamsi);   
                  setVal(dateObj?.toDate?.()?.toISOString()); 
              }}


              />
              <div className="absolute top-1/2 -translate-y-1/2 left-3 pointer-events-none">
                <IoCalendarOutline size={20} className="text-gray-500" />
              </div>
            </div>
  
            {hasError && (
              <div role="alert" className="flex items-center gap-2 mt-1 text-sm text-red-600">
                <AiOutlineExclamationCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}
          </motion.div>
        );
      }
  
      /* ========================= TEXTAREA ========================= */
      if (type === "text") {
        return (
          <motion.div
            ref={wrapperRef}
            animate={shakeAnimation}
            transition={{ duration: 0.56 }}
            className={`flex flex-col gap-2 ${containerClass}`}
            dir="rtl"
          >
            <label className="text-gray-700 text-[18px]">{label}</label>
            <textarea
              ref={inputRef}
              rows={textRows}
              value={ displayValue ?? ""}
              onChange={(e) => {
                setDisplayValue(e.target.value);
                setVal?.(e.target.value);
                setSubVal?.(null);
              }}
              placeholder="متن را وارد کنید..."
              className={`w-full rounded-xl p-3 resize-vertical outline-none text-[15px] border ${hasError ? "border-red-500" : "border-gray-300"}`}
            />
            {displayValue ? (
              <div className="text-right">
                <button
                  onMouseDown={handleClearMouseDown}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  پاک کردن
                </button>
              </div>
            ) : null}
            {hasError && (
              <div role="alert" className="flex items-center gap-2 mt-1 text-sm text-red-600">
                <AiOutlineExclamationCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}
          </motion.div>
        );
      }
  
      /* ========================= NUMBER ========================= */
      if (type === "number") {
        return (
          <motion.div
            ref={wrapperRef}
            animate={shakeAnimation}
            transition={{ duration: 0.56 }}
            className={`flex flex-col gap-2 ${containerClass}`}
            dir="rtl"
          >
            <label className="text-gray-700 text-[18px]">{label}</label>

            <div className={`flex items-center justify-between w-full rounded-2xl h-[44px] px-3 bg-white border ${hasError ? "border-red-500" : "border-gray-300"}`}>
              <input
                ref={inputRef}
                inputMode="decimal"
                value={isNaN(displayValue) ? displayValue : formatNumberForDisplay( displayValue) ?? ""}
                onChange={(e) => {
                  const rawInput = e.target.value;
                  const rawNormalized = normalizeNumericRaw(rawInput);
                  const formatted = formatNumberForDisplay(rawNormalized);
                  setDisplayValue(formatted);
                  setVal?.(rawNormalized === "" ? null : rawNormalized);
                  setSubVal?.(null);
                }}
                placeholder="عدد را وارد کنید..."
                className="flex-1 h-full outline-none bg-transparent text-[15px] p-1"
              />

              <div className="flex items-center gap-2 pl-1">
                {unitLabel ? (
                  <span className="text-gray-600 text-sm whitespace-nowrap">{unitLabel}</span>
                ) : null}

   
              </div>
            </div>

            {hasError && (
              <div role="alert" className="flex items-center gap-2 mt-1 text-sm text-red-600">
                <AiOutlineExclamationCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}
          </motion.div>
        );
      }
  
      /* ========================= DEFAULT ========================= */
      return (
        <motion.div
          ref={wrapperRef}
          animate={shakeAnimation}
          transition={{ duration: 0.56 }}
          className={`flex flex-col gap-2 ${containerClass}`}
          dir="rtl"
        >
          <label className="text-gray-700 text-[18px]">{label}</label>
          <div className={`flex items-center w-full rounded-2xl border h-[44px] px-3 bg-white ${hasError ? "border-red-500" : "border-gray-300"}`}>
            <input
              ref={inputRef}
              value={val ? val : displayValue ?? ""}
              onChange={(e) => {
                setDisplayValue(e.target.value);
                setVal?.(e.target.value);
                setSubVal?.(null);
              }}
              placeholder={Placeholder}
              className="w-full h-full outline-none bg-transparent text-[15px] p-1"
            />
            {displayValue ? (
              <AiOutlineClose size={18} className="text-gray-600 hover:text-gray-800" onMouseDown={handleClearMouseDown} />
            ) : null}
          </div>
  
          {hasError && (
            <div role="alert" className="flex items-center gap-2 mt-1 text-sm text-red-600">
              <AiOutlineExclamationCircle size={16} />
              <span>{errorMessage}</span>
            </div>
          )}
        </motion.div>
      );
    }
  );
  
  export default CustomInput;
  